/**
 * Initials-based avatar with solid color backgrounds.
 *
 * 12 color combinations: 4 from the design system + 8 curated extras.
 * Each palette entry has a background and a foreground (white or black)
 * chosen for sufficient contrast.
 */

import { createAvatar } from '@dicebear/core'
import * as notionists from '@dicebear/notionists'

const AVATAR_PALETTES = [
  // Design system colors
  { bg: '#0A2120', fg: '#FFFFFF' }, // --platform-deep (dark teal)
  { bg: '#0B2D2E', fg: '#FFFFFF' }, // --cta / dark card
  { bg: '#B4F9DB', fg: '#0A2120' }, // --platform-card (mint)
  { bg: '#00C9A7', fg: '#0A2120' }, // --chart-primary (teal)
  // Curated extras
  { bg: '#5B5EA6', fg: '#FFFFFF' }, // muted indigo
  { bg: '#E8505B', fg: '#FFFFFF' }, // warm coral
  { bg: '#F9C846', fg: '#0A2120' }, // golden amber
  { bg: '#3D5A80', fg: '#FFFFFF' }, // slate blue
  { bg: '#9B5DE5', fg: '#FFFFFF' }, // soft purple
  { bg: '#F15BB5', fg: '#FFFFFF' }, // vivid pink
  { bg: '#00BBF9', fg: '#0A2120' }, // sky blue
  { bg: '#2D6A4F', fg: '#FFFFFF' }, // forest green
] as const

export const AVATAR_PALETTE_COUNT = AVATAR_PALETTES.length

export interface AvatarPalette {
  bg: string
  fg: string
}

/**
 * Resolve a seed string to a deterministic palette index.
 * The same seed always produces the same color.
 */
export function seedToPaletteIndex(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0
  }
  return ((hash % AVATAR_PALETTES.length) + AVATAR_PALETTES.length) % AVATAR_PALETTES.length
}

/**
 * Get a palette by index (wraps around).
 */
export function getAvatarPalette(index: number): AvatarPalette {
  return AVATAR_PALETTES[((index % AVATAR_PALETTES.length) + AVATAR_PALETTES.length) % AVATAR_PALETTES.length]
}

/**
 * Get the palette for a given seed string.
 */
export function getAvatarPaletteForSeed(seed: string): AvatarPalette {
  return getAvatarPalette(seedToPaletteIndex(seed))
}

/**
 * Derive display initials from a user's name or email.
 * Returns first letter of first name + first letter of last name.
 */
export function getUserInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return parts[0][0]?.toUpperCase() ?? ''
  }
  if (email) {
    return email[0].toUpperCase()
  }
  return ''
}

/**
 * Capitalize the first letter of a string (e.g., "admin" → "Admin").
 */
export function capitalizeFirst(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Derive a user-facing display name from a profile object.
 * Fallback chain: name → email prefix → empty string.
 */
export function getDisplayName(profile: { name?: string; email?: string } | null | undefined): string {
  if (!profile) return ''
  const raw = profile.name || profile.email?.split('@')[0] || ''
  return capitalizeFirst(raw.split(' ')[0])
}

/**
 * Deterministic illustrated avatar (Dicebear Notionists) for a seed such as avatarSeed or email.
 * Returns null on failure so callers can fall back to initials.
 */
export function getNotionistAvatar(
  seed: string,
  options?: { backgroundColor?: string[] },
): string | null {
  try {
    const avatar = createAvatar(notionists, {
      seed,
      radius: 50,
      backgroundColor: options?.backgroundColor ?? ['262626'],
    })
    return avatar.toDataUri()
  } catch {
    return null
  }
}
